import get from 'lodash.get';

import constants from '../../../constants';
import { listFilterOperator } from '../../../constants/aggregate';
import type { ISearchFilter } from '../../../models/search/v3/searchFilter';
import type {
  TicketingSalesBreakdownItem,
  TicketingSalesData
} from '../../entity/performance/types';

const saleDetailTypeTicket = 'ticket';

const getHeaders = (): Record<string, string> => {
  const raw = localStorage['session'];
  const session = raw && raw !== 'undefined' ? JSON.parse(raw) : null;
  return {
    ApiKey: constants.apikey || '',
    'Content-Type': 'application/json',
    Authorization: session?.key ? `bearer ${session.key}` : ''
  };
};

/**
 * Count the campaign's checked-in ticket holders. Returned tickets keep their
 * check-in time, so they are excluded to match ticketing.ticketCap.sold.
 * Resolves null when the count can't be loaded so the widget hides the row.
 */
export const fetchCheckedInCount = async (
  campaignId: string
): Promise<number | null> => {
  const filters: ISearchFilter[] = [
    {
      key: 'sale.campaignId',
      operator: listFilterOperator.EQUAL_TO,
      value: [campaignId]
    },
    {
      key: 'type',
      operator: listFilterOperator.EQUAL_TO,
      value: [saleDetailTypeTicket]
    },
    {
      key: 'financials.returned',
      operator: listFilterOperator.EQUAL_TO,
      value: ['false']
    },
    { key: 'checkedInAt', operator: listFilterOperator.IS_NOT_NULL, value: [] }
  ];
  try {
    const response = await fetch(
      `${constants.baseApiHost}/api/v3/saleDetail/search`,
      {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
          page: 1,
          pageSize: 1,
          resultType: 'Basic',
          filters
        })
      }
    );
    if (!response.ok) return null;
    const result = await response.json();
    const totalCount = get(result, 'data.totalCount');
    return typeof totalCount === 'number' ? totalCount : null;
  } catch {
    return null;
  }
};

/**
 * Build the lifetime ("to date") ticketing snapshot for the performance widget
 * from the loaded campaign, ticket and merchandise records. Per-type figures
 * come from each item's `inventory.sold` and are not time-period filtered.
 */
export const buildTicketingSales = (state: unknown): TicketingSalesData | null => {
  const campaign = get(state, 'campaign.record.data.data', null);
  if (!campaign) return null;

  const ticketCap = get(campaign, 'ticketing.ticketCap', null) as TicketingSalesData['ticketCap'];

  const tickets = (get(state, 'campaign.ticketsRecord.data.data.list', []) || []) as any[];
  const merchandise = (get(state, 'campaign.merchandiseRecord.data.data.list', []) || []) as any[];

  const ticketTypes: TicketingSalesBreakdownItem[] = [...tickets]
    .sort(
      (a, b) =>
        (get(a, 'template.value.sequence', 0) as number) -
        (get(b, 'template.value.sequence', 0) as number)
    )
    .map((ticket) => ({
      name: (get(ticket, 'name') as string) || '',
      sold: (get(ticket, 'inventory.sold', 0) as number) || 0
    }));

  const merchandiseItems: TicketingSalesBreakdownItem[] = merchandise.map((item) => {
    const itemSold = get(item, 'inventory.sold');
    const sold =
      typeof itemSold === 'number'
        ? itemSold
        : (get(item, 'skus', []) as any[]).reduce(
            (sum, sku) => sum + ((get(sku, 'inventory.sold', 0) as number) || 0),
            0
          );
    return { name: (get(item, 'name') as string) || '', sold };
  });

  return {
    enabled: !!get(campaign, 'ticketing.enabled'),
    ticketCap,
    ticketsSold: get(campaign, 'ticketing.ticketCap.sold', null) as number | null,
    merchandiseSold: get(campaign, 'saleDetailMerchandiseCount', null) as number | null,
    ticketTypes,
    merchandise: merchandiseItems
  };
};
